import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal } from '@carbon/react';
import { Checkmark, Close, Renew } from '@carbon/icons-react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { PageHeader, ErrorState, BlockSkeleton, InlineError } from '../components/states';
import { Money, When } from '../components/format';
import { CommandStatus, COMMAND_TYPE, HE_A_ERRORS } from '../components/labels';
import { ConfigChange } from '../components/ConfigChange';
import { LodeChange } from '../components/LodeChange';
import { commandSummary, type Command } from './Commands';
import { mayDecide } from './commandDecide';

type Detail = {
  command: Command & { approverId: string | null; decidedAt: string | null; requestId: string };
};

export function CommandDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['command', id], queryFn: () => api.get<Detail>('/api/admin/commands/' + id) });
  const [dialog, setDialog] = useState<'approve' | 'reject' | null>(null);

  const decide = useMutation({
    mutationFn: (kind: 'approve' | 'reject') => api.post(`/api/admin/commands/${id}/${kind}`, {}),
    onSuccess: () => { setDialog(null); qc.invalidateQueries({ queryKey: ['command'] }); qc.invalidateQueries({ queryKey: ['commands'] }); },
  });
  const retry = useMutation({
    mutationFn: () => api.post(`/api/admin/commands/${id}/retry`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['command', id] }),
  });

  if (q.isLoading) return <div className="page"><BlockSkeleton lines={8} /></div>;
  if (q.error || !q.data) return <div className="page"><ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;
  const { command: c } = q.data;
  // a command left waiting from before approval was removed: any admin with its approve permission,
  // its maker included, applies or rejects it (the API's rule, commandDecide.ts)
  const decidable = mayDecide(c, can);

  return (
    <div className="page">
      <PageHeader
        title={`Lệnh ${COMMAND_TYPE[c.type] ?? c.type}`}
        lead={<>Khóa lệnh <span className="num">{c.key}</span></>}
        actions={<>
          {decidable ? <>
            <Button kind="primary" renderIcon={Checkmark} onClick={() => setDialog('approve')}>Duyệt</Button>
            <Button kind="danger--tertiary" renderIcon={Close} onClick={() => setDialog('reject')}>Từ chối</Button>
          </> : null}
          {c.status === 'PENDING' ? <Button kind="tertiary" renderIcon={Renew} onClick={() => retry.mutate()} disabled={retry.isPending}>Gửi lại ngay</Button> : null}
        </>}
      />
      {c.status === 'AWAITING_APPROVAL' ? (
        <p className="muted" style={{ marginBottom: 16 }}>Lệnh này tạo từ trước khi bỏ bước duyệt nên còn chờ. Không có gì tự chạy: người có quyền duyệt (kể cả người tạo) bấm Duyệt để thực hiện, hoặc Từ chối để hủy.</p>
      ) : null}
      <div className="split">
        <section>
          <dl className="facts">
            <dt>Trạng thái</dt><dd><CommandStatus s={c.status} /></dd>
            <dt>Nội dung</dt><dd>{commandSummary(c)}</dd>
            {c.type !== 'CONFIG' ? <><dt>Người chơi</dt><dd>{c.targetUserId ? <Link className="cell-link" to={`/users/${c.targetUserId}`}>{c.targetUserId}</Link> : '-'}</dd></> : null}
            <dt>Tạo lúc</dt><dd><When v={c.createdAt} /></dd>
            {c.lastError ? <><dt>Lỗi gần nhất</dt><dd>{HE_A_ERRORS[c.lastError] ?? c.lastError}</dd></> : null}
            <dt>Xong lúc</dt><dd><When v={c.completedAt} /></dd>
          </dl>
          <div className="section">
            <h2>Kết quả từ hệ A</h2>
            {c.result ? <pre className="pre">{JSON.stringify(c.result, null, 2)}</pre> : <p className="muted">Chưa có kết quả.</p>}
          </div>
        </section>
        {c.type === 'CONFIG' ? (
          <section className="section section--flush">
            <h2>Cấu hình sẽ ghi lên hệ A</h2>
            <ConfigChange c={c} />
          </section>
        ) : c.type === 'LODE' ? (
          <section className="section section--flush">
            <h2>Lệnh lô đề</h2>
            <LodeChange c={c} />
          </section>
        ) : null}
      </div>
      <Modal open={dialog !== null} modalHeading={dialog === 'approve' ? 'Duyệt lệnh này?' : 'Từ chối lệnh này?'}
        primaryButtonText={dialog === 'approve' ? 'Duyệt và gửi' : 'Từ chối'} secondaryButtonText="Hủy" danger={dialog === 'reject'}
        primaryButtonDisabled={decide.isPending}
        onRequestClose={() => setDialog(null)} onRequestSubmit={() => dialog && decide.mutate(dialog)}>
        <div className="stack">
          <p>{commandSummary(c)}{c.targetUserId ? <> cho người chơi {c.targetUserId}</> : null}</p>
          {c.type === 'CONFIG' ? <ConfigChange c={c} /> : null}
          {c.type === 'LODE' ? <LodeChange c={c} /> : null}
          <InlineError error={decide.error} />
        </div>
      </Modal>
    </div>
  );
}
